import * as React from 'react'
import some from 'lodash/some'
import {startOfMonth, endOfMonth, startOfDay, endOfDay, isAfter} from 'date-fns'

import {Button} from '@frontrow/console/src/components/button'
import Input from '@frontrow/console/src/entities/components/base/input'
import {CalendarDatePicker} from '@frontrow/console/src/components/calendar-date-picker'
import {type LicenseT} from '@frontrow/console/src/entities/models/license'

import FormField from '@frontrow/console/src/components/common/form-field'
import {type MultipleSubjectLicensesT} from '@frontrow/console/src/components/common/school-licenses-list'
import * as style from './new-license-row.module.scss'

type Props = {
  license: LicenseT | undefined | null
  schoolName: string
  addLicenses: (licenseData: MultipleSubjectLicensesT) => void
  onDelete: () => void
}

type State = {
  startsAt: Date
  expiresAt: Date
  numMathLicenses: number
  numElaLicenses: number
  numSocialStudiesLicenses: number
  numScienceLicenses: number
}

function getDefaultLicensesState(): State {
  return {
    startsAt: startOfMonth(new Date()),
    expiresAt: endOfMonth(new Date()),
    numMathLicenses: NaN,
    numElaLicenses: NaN,
    numSocialStudiesLicenses: NaN,
    numScienceLicenses: NaN
  }
}

export default function NewLicenseRow(props: Props): React.ReactElement {
  const {license, schoolName, onDelete, addLicenses} = props

  const getInitialState = (): State => {
    const subjectToCount = {
      math: 'numMathLicenses',
      ela: 'numElaLicenses',
      social_studies: 'numSocialStudiesLicenses',
      science: 'numScienceLicenses'
    }

    if (license === null || license === undefined) {
      return getDefaultLicensesState()
    } else {
      return {
        startsAt: license.startsAt,
        expiresAt: license.expiresAt,
        numMathLicenses: NaN,
        numElaLicenses: NaN,
        numSocialStudiesLicenses: NaN,
        numScienceLicenses: NaN,
        [subjectToCount[license.subject]]: license.count
      }
    }
  }

  const [formData, setFormData] = React.useState<State>(getInitialState)

  const createDateOnSelect = (dateField: string): ((val?: Date | null) => void) => {
    return (date?: Date | null) => {
      setFormData(prevState => ({
        ...prevState,
        [dateField]: date
      }))
    }
  }

  const createFieldOnChange = (field: string): ((val: string) => void) => {
    return (newValue: string) => {
      setFormData(prevState => ({
        ...prevState,
        [field]: parseInt(newValue, 10)
      }))
    }
  }

  const isValidLicensesData = (): boolean => {
    const {
      startsAt,
      expiresAt,
      numMathLicenses,
      numElaLicenses,
      numSocialStudiesLicenses,
      numScienceLicenses
    } = formData

    const atLeastOneValidSubject = some(
      [numMathLicenses, numElaLicenses, numSocialStudiesLicenses, numScienceLicenses],
      licenseCount => !isNaN(licenseCount) && licenseCount > 0
    )

    return (
      atLeastOneValidSubject &&
      startsAt !== null &&
      expiresAt !== null &&
      isAfter(expiresAt, startsAt)
    )
  }

  const addNewLicenses = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const {
      startsAt,
      expiresAt,
      numMathLicenses,
      numElaLicenses,
      numSocialStudiesLicenses,
      numScienceLicenses
    } = formData

    if (isValidLicensesData()) {
      addLicenses({
        numMathLicenses,
        numElaLicenses,
        numSocialStudiesLicenses,
        numScienceLicenses,
        startsAt: startOfDay(startsAt),
        expiresAt: endOfDay(expiresAt)
      })
    }
  }

  const {
    startsAt,
    expiresAt,
    numMathLicenses,
    numElaLicenses,
    numSocialStudiesLicenses,
    numScienceLicenses
  } = formData

  const licenseCountPlaceHolder = '# seats'

  return (
    <form action="" onSubmit={addNewLicenses} className={style.newLicenseContainer}>
      <Button style="link" addClass={style.deleteButton} onClick={onDelete}>
        <i className="fa fa-trash fa-lg" />
      </Button>
      <FormField wrapperStyle={style.dateFieldWrapper} label="Start date">
        <CalendarDatePicker
          id={`${schoolName}-license-start-date`}
          selectedDate={startsAt}
          onSelectDate={createDateOnSelect('startsAt')}
        />
      </FormField>
      <FormField wrapperStyle={style.dateFieldWrapper} label="Expiration date">
        <CalendarDatePicker
          id={`${schoolName}-license-expires-at`}
          selectedDate={expiresAt}
          onSelectDate={createDateOnSelect('expiresAt')}
        />
      </FormField>
      <FormField wrapperStyle={style.licenseCountFieldWrapper} label="Math">
        <Input
          value={isNaN(numMathLicenses) ? '' : numMathLicenses}
          type="number"
          placeholder={licenseCountPlaceHolder}
          onChange={createFieldOnChange('numMathLicenses')}
        />
      </FormField>
      <FormField wrapperStyle={style.licenseCountFieldWrapper} label="ELA">
        <Input
          value={isNaN(numElaLicenses) ? '' : numElaLicenses}
          type="number"
          placeholder={licenseCountPlaceHolder}
          onChange={createFieldOnChange('numElaLicenses')}
        />
      </FormField>
      <FormField wrapperStyle={style.licenseCountFieldWrapper} label="SS">
        <Input
          value={isNaN(numSocialStudiesLicenses) ? '' : numSocialStudiesLicenses}
          type="number"
          placeholder={licenseCountPlaceHolder}
          onChange={createFieldOnChange('numSocialStudiesLicenses')}
        />
      </FormField>
      <FormField wrapperStyle={style.licenseCountFieldWrapper} label="Science">
        <Input
          value={isNaN(numScienceLicenses) ? '' : numScienceLicenses}
          type="number"
          placeholder={licenseCountPlaceHolder}
          onChange={createFieldOnChange('numScienceLicenses')}
        />
      </FormField>
      <Button
        style="primary"
        type="submit"
        addClass={style.addLicenseButton}
        disabled={!isValidLicensesData()}
      >
        Add license
      </Button>
    </form>
  )
}
